// ATTP-HR — Enterprise HCM workspace.
// Single shell with sidebar + sub-pages. Role-aware (super_admin & org_manager get full access).
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserPlus, LayoutDashboard, Building2, Briefcase, Clock, Calendar,
  Wallet, Target, BookOpen, FileText, Workflow, Settings, ChevronRight,
  Search, Filter, Plus, RefreshCw, Edit2, Trash2, X, CheckCircle2, XCircle,
  ArrowLeft, BarChart3, TrendingUp, AlertCircle, CreditCard, Cpu, ShieldCheck,
  DoorOpen, ScanLine, AlertTriangle, Wifi, HeartPulse, Stethoscope, Upload, Download,
  Receipt, Package, Megaphone, Sparkles,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { themeFor } from '@/lib/moduleThemes';
import EmployeeSearchPicker from '@/components/hr/EmployeeSearchPicker';

const T = themeFor('hr');

// ===== Nav structure =====
const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
  { key: 'employees', label: 'Employees', icon: Users, group: 'Core HR' },
  { key: 'departments', label: 'Departments', icon: Building2, group: 'Core HR' },
  { key: 'positions', label: 'Positions', icon: Briefcase, group: 'Core HR' },
  { key: 'attendance', label: 'Attendance', icon: Clock, group: 'Workforce' },
  { key: 'leave', label: 'Leave', icon: Calendar, group: 'Workforce' },
  { key: 'payroll', label: 'Payroll', icon: Wallet, group: 'Workforce' },
  { key: 'performance', label: 'Performance', icon: Target, group: 'Talent' },
  { key: 'learning', label: 'Learning', icon: BookOpen, group: 'Talent' },
  { key: 'vacancies', label: 'Vacancies', icon: UserPlus, group: 'Recruitment' },
  { key: 'candidates', label: 'Candidates', icon: Users, group: 'Recruitment' },
  { key: 'cards', label: 'Cards', icon: CreditCard, group: 'Identity & Access' },
  { key: 'devices', label: 'Devices', icon: Cpu, group: 'Identity & Access' },
  { key: 'access-zones', label: 'Access Zones', icon: DoorOpen, group: 'Identity & Access' },
  { key: 'access-rules', label: 'Access Rules', icon: ShieldCheck, group: 'Identity & Access' },
  { key: 'visitors', label: 'Visitors', icon: ScanLine, group: 'Security' },
  { key: 'incidents', label: 'Incidents', icon: AlertTriangle, group: 'Security' },
  { key: 'access-events', label: 'Access Events', icon: Wifi, group: 'Security' },
  { key: 'hmo-providers', label: 'HMO Providers', icon: HeartPulse, group: 'Medical & HMO' },
  { key: 'hmo-hospitals', label: 'Hospitals', icon: Stethoscope, group: 'Medical & HMO' },
  { key: 'hmo-enrollments', label: 'HMO Enrollments', icon: Users, group: 'Medical & HMO' },
  { key: 'hmo-change-requests', label: 'Change Requests', icon: RefreshCw, group: 'Medical & HMO' },
  { key: 'assets', label: 'Assets', icon: Package, group: 'Operations' },
  { key: 'invoices', label: 'Invoices', icon: Receipt, group: 'Operations' },
  { key: 'receipts', label: 'Receipts', icon: Wallet, group: 'Operations' },
  { key: 'announcements', label: 'Announcements', icon: Megaphone, group: 'Communications' },
  { key: 'ai-compliance', label: 'AI Compliance', icon: Sparkles, group: 'AI Assistants' },
  { key: 'ai-screening', label: 'AI Screening', icon: Sparkles, group: 'AI Assistants' },
  { key: 'workflows', label: 'Workflows', icon: Workflow, group: 'Governance' },
  { key: 'documents', label: 'Documents', icon: FileText, group: 'Governance' },
  { key: 'templates', label: 'Bulk Templates', icon: Upload, group: 'Governance' },
  { key: 'config', label: 'Config Center', icon: Settings, group: 'Governance' },
];

export default function HRShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [active, setActive] = useState('dashboard');

  const groups = NAV.reduce((acc, n) => {
    acc[n.group] = acc[n.group] || []; acc[n.group].push(n); return acc;
  }, {});

  return (
    <div className="min-h-screen flex" style={{ background: T.bg }} data-testid="hr-shell">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 border-r" style={{ background: T.primary, color: T.textOnPrimary }}>
        <div className="px-4 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <button onClick={() => navigate(user?.role === 'super_admin' ? '/control-panel' : '/team')}
            className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-75 hover:opacity-100 mb-3">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="font-display font-bold text-lg">ATTP-HR</div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: T.accent }}>Enterprise HCM</div>
        </div>
        <nav className="px-2 py-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 100px)' }}>
          {Object.entries(groups).map(([g, items]) => (
            <div key={g} className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] px-2 mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>{g}</div>
              {items.map((item) => {
                const I = item.icon;
                const isActive = active === item.key;
                return (
                  <button key={item.key} onClick={() => setActive(item.key)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded text-sm transition-all ${isActive ? 'shadow-sm' : 'hover:bg-white/5'}`}
                    style={isActive
                      ? { background: T.accent, color: T.primary, fontWeight: 600 }
                      : { color: 'rgba(255,255,255,0.8)' }}
                    data-testid={`hr-nav-${item.key}`}>
                    <I className="w-4 h-4" strokeWidth={1.8} />
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <header className="bg-white border-b px-6 py-3 flex items-center justify-between">
          <div className="font-display font-semibold text-navy">
            {NAV.find((n) => n.key === active)?.label}
          </div>
          <div className="flex items-center gap-3 text-xs text-neutral-500">
            <span>{user?.email}</span>
            <span className="px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ background: `${T.accent}20`, color: T.primary }}>
              {user?.role}
            </span>
          </div>
        </header>
        <div className="p-6">
          {active === 'dashboard' && <Dashboard />}
          {active === 'employees' && <Employees />}
          {active === 'departments' && <SimpleCrud kind="departments" title="Departments" fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'code', label: 'Code' }, { key: 'notes', label: 'Notes', type: 'textarea' },
          ]} cols={['name', 'code']} />}
          {active === 'positions' && <SimpleCrud kind="positions" title="Positions" fields={[
            { key: 'title', label: 'Title', required: true },
            { key: 'grade', label: 'Grade' },
            { key: 'status', label: 'Status', type: 'select', options: ['active', 'frozen', 'vacant', 'filled'] },
          ]} cols={['title', 'grade', 'status']} />}
          {active === 'attendance' && <Attendance />}
          {active === 'leave' && <Leave />}
          {active === 'payroll' && <Payroll />}
          {active === 'performance' && <SimpleCrud kind="performance" title="Performance Reviews" fields={[
            { key: 'employee_id', label: 'Employee ID', required: true },
            { key: 'period', label: 'Period (e.g. 2026-Q1)', required: true },
            { key: 'overall_score', label: 'Overall score', type: 'number' },
            { key: 'rating', label: 'Rating', type: 'select', options: ['outstanding', 'exceeds', 'meets', 'below', 'unsatisfactory'] },
            { key: 'goals_met', label: 'Goals met', type: 'textarea' },
          ]} cols={['employee_id', 'period', 'rating', 'overall_score']} />}
          {active === 'learning' && <Learning />}
          {active === 'vacancies' && <SimpleCrud kind="vacancies" title="Vacancies" fields={[
            { key: 'title', label: 'Role title', required: true },
            { key: 'location', label: 'Location' },
            { key: 'headcount', label: 'Headcount', type: 'number' },
            { key: 'closing_date', label: 'Closing date', type: 'date' },
            { key: 'status', label: 'Status', type: 'select', options: ['open', 'shortlisting', 'closed', 'cancelled'] },
            { key: 'description', label: 'Description', type: 'textarea' },
            { key: 'requirements', label: 'Requirements', type: 'textarea' },
          ]} cols={['ref_no', 'title', 'location', 'headcount', 'status']} />}
          {active === 'candidates' && <Candidates />}
          {active === 'cards' && <SimpleCrud kind="cards" title="Card Registry" fields={[
            { key: 'card_number', label: 'Card number', required: true },
            { key: 'card_type', label: 'Type', type: 'select', options: ['rfid', 'nfc', 'mifare', 'prox', 'smart', 'qr', 'barcode'] },
            { key: 'card_uid', label: 'Card UID (read from device)' },
            { key: 'employee_id', label: 'Assigned employee ID' },
            { key: 'status', label: 'Status', type: 'select', options: ['active', 'suspended', 'lost', 'replaced', 'retired'] },
            { key: 'issue_date', label: 'Issue date', type: 'date' },
            { key: 'expiry_date', label: 'Expiry date', type: 'date' },
            { key: 'notes', label: 'Notes', type: 'textarea' },
          ]} cols={['card_number', 'card_type', 'card_uid', 'status']} />}
          {active === 'devices' && <Devices />}
          {active === 'access-zones' && <SimpleCrud kind="access-zones" title="Access Zones" fields={[
            { key: 'name', label: 'Zone name', required: true },
            { key: 'location', label: 'Location' },
            { key: 'description', label: 'Description', type: 'textarea' },
          ]} cols={['name', 'location']} />}
          {active === 'access-rules' && <SimpleCrud kind="access-rules" title="Access Rules" fields={[
            { key: 'employee_id', label: 'Employee ID (leave blank for all)' },
            { key: 'department_id', label: 'Department ID (leave blank for all)' },
            { key: 'zone_id', label: 'Zone ID', required: true },
            { key: 'start_time', label: 'Allowed start time (HH:MM)' },
            { key: 'end_time', label: 'Allowed end time (HH:MM)' },
            { key: 'active', label: 'Active', type: 'select', options: ['true', 'false'] },
          ]} cols={['zone_id', 'employee_id', 'department_id', 'active']} />}
          {active === 'visitors' && <Visitors />}
          {active === 'incidents' && <SimpleCrud kind="incidents" title="Security Incidents" fields={[
            { key: 'kind', label: 'Kind', type: 'select', required: true, options: ['theft', 'trespass', 'lost_property', 'access_violation', 'safety', 'emergency', 'visitor_breach', 'other'] },
            { key: 'severity', label: 'Severity', type: 'select', options: ['low', 'medium', 'high', 'critical'] },
            { key: 'title', label: 'Title', required: true },
            { key: 'description', label: 'Description', type: 'textarea' },
            { key: 'location', label: 'Location' },
            { key: 'status', label: 'Status', type: 'select', options: ['open', 'investigating', 'resolved', 'closed'] },
            { key: 'resolution', label: 'Resolution', type: 'textarea' },
          ]} cols={['ref_no', 'kind', 'severity', 'title', 'status']} />}
          {active === 'access-events' && <AccessEvents />}
          {active === 'hmo-providers' && <SimpleCrud kind="hmo/providers" title="HMO Providers" fields={[
            { key: 'name', label: 'Provider name', required: true },
            { key: 'code', label: 'Short code' },
            { key: 'contact_phone', label: 'Contact phone' },
            { key: 'contact_email', label: 'Contact email' },
            { key: 'coverage_tier', label: 'Tier', type: 'select', options: ['basic', 'standard', 'premium', 'enterprise'] },
            { key: 'premium_per_employee', label: 'Premium / employee (₦)', type: 'number' },
            { key: 'active', label: 'Active', type: 'select', options: ['true', 'false'] },
            { key: 'notes', label: 'Notes', type: 'textarea' },
          ]} cols={['name', 'code', 'coverage_tier', 'premium_per_employee']} />}
          {active === 'hmo-hospitals' && <SimpleCrud kind="hmo/hospitals" title="HMO Hospitals" fields={[
            { key: 'name', label: 'Hospital name', required: true },
            { key: 'provider_id', label: 'Provider ID', required: true },
            { key: 'location', label: 'Location / city' },
            { key: 'address', label: 'Address' },
            { key: 'phone', label: 'Phone' },
            { key: 'active', label: 'Active', type: 'select', options: ['true', 'false'] },
          ]} cols={['name', 'location', 'provider_id']} />}
          {active === 'hmo-enrollments' && <SimpleCrud kind="hmo/enrollments" title="Employee HMO Enrollments" fields={[
            { key: 'employee_id', label: 'Employee (search by name)', type: 'employee', required: true },
            { key: 'provider_id', label: 'Provider ID', required: true },
            { key: 'hospital_id', label: 'Primary hospital ID' },
            { key: 'plan_tier', label: 'Plan tier', type: 'select', options: ['basic', 'standard', 'premium', 'enterprise'] },
            { key: 'member_number', label: 'Member number' },
            { key: 'enrollment_date', label: 'Enrolled on', type: 'date' },
            { key: 'renewal_date', label: 'Renews on', type: 'date' },
            { key: 'status', label: 'Status', type: 'select', options: ['active', 'suspended', 'cancelled', 'pending_change'] },
          ]} cols={['employee_id', 'provider_id', 'plan_tier', 'status']} />}
          {active === 'hmo-change-requests' && <HMOChangeRequests />}
          {active === 'assets' && <SimpleCrud kind="assets" title="Asset Register" fields={[
            { key: 'name', label: 'Asset name', required: true },
            { key: 'category', label: 'Category', type: 'select', options: ['laptop', 'phone', 'vehicle', 'furniture', 'equipment', 'software'] },
            { key: 'serial_number', label: 'Serial number' },
            { key: 'purchase_date', label: 'Purchase date', type: 'date' },
            { key: 'purchase_value', label: 'Purchase value (₦)', type: 'number' },
            { key: 'current_value', label: 'Current value (₦)', type: 'number' },
            { key: 'supplier', label: 'Supplier' },
            { key: 'location', label: 'Location' },
            { key: 'condition', label: 'Condition', type: 'select', options: ['new', 'good', 'fair', 'poor', 'damaged'] },
            { key: 'status', label: 'Status', type: 'select', options: ['available', 'assigned', 'under_repair', 'retired', 'lost'] },
            { key: 'assigned_to_employee_id', label: 'Assign to employee (search by name)', type: 'employee' },
            { key: 'notes', label: 'Notes', type: 'textarea' },
          ]} cols={['tag', 'name', 'category', 'condition', 'status']} />}
          {active === 'invoices' && <InvoicesTab />}
          {active === 'receipts' && <ReceiptsTab />}
          {active === 'announcements' && <SimpleCrud kind="announcements" title="Announcements" fields={[
            { key: 'title', label: 'Title', required: true },
            { key: 'body', label: 'Message', type: 'textarea', required: true },
            { key: 'kind', label: 'Type', type: 'select', options: ['manual', 'policy', 'emergency', 'celebration'] },
            { key: 'priority', label: 'Priority', type: 'select', options: ['low', 'normal', 'high', 'urgent'] },
            { key: 'pinned', label: 'Pin to top', type: 'select', options: ['true', 'false'] },
            { key: 'expires_at', label: 'Expires at', type: 'date' },
          ]} cols={['title', 'priority', 'kind', 'published_at']} />}
          {active === 'ai-compliance' && <AICompliance />}
          {active === 'ai-screening' && <AIScreening />}
          {active === 'workflows' && <Workflows />}
          {active === 'documents' && <SimpleCrud kind="documents" title="HR Documents" fields={[
            { key: 'title', label: 'Title', required: true },
            { key: 'kind', label: 'Type', type: 'select', options: ['cv', 'contract', 'certificate', 'license', 'medical', 'payslip', 'warning', 'other'] },
            { key: 'employee_id', label: 'Employee (search by name)', type: 'employee' },
            { key: 'issue_date', label: 'Issue date', type: 'date' },
            { key: 'expiry_date', label: 'Expiry date', type: 'date' },
            { key: 'file_url', label: 'File URL' },
          ]} cols={['title', 'kind', 'issue_date', 'expiry_date']} />}
          {active === 'templates' && <TemplatesTab />}
          {active === 'config' && <ConfigCenter />}
        </div>
      </main>
    </div>
  );
}

/* ============== Dashboard ============== */
function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get('/hr/dashboard'); setData(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  if (loading) return <div className="text-neutral-500">Loading…</div>;
  if (!data || !data.total_employees) return <EmptyState title="No HR data yet" desc="Add your first employee, or ask the super-admin to seed sample data from the Control Panel." />;

  const cards = [
    { label: 'Total employees', value: data.total_employees, icon: Users },
    { label: 'Active', value: data.active, icon: CheckCircle2 },
    { label: 'On probation', value: data.probation, icon: AlertCircle },
    { label: 'On leave', value: data.on_leave, icon: Calendar },
    { label: 'Open vacancies', value: data.vacancies_open, icon: UserPlus },
    { label: 'Pending approvals', value: data.pending_leaves + data.pending_workflows, icon: Workflow },
  ];

  return (
    <div data-testid="hr-dashboard">
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Executive Command Centre</h2>
        <p className="text-sm text-neutral-500">Live workforce metrics across your organisation.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {cards.map((c) => {
          const I = c.icon;
          return (
            <div key={c.label} className="p-4 bg-white rounded border">
              <I className="w-5 h-5 mb-2" style={{ color: T.accent }} />
              <div className="font-display font-bold text-2xl" style={{ color: T.primary }}>{c.value ?? 0}</div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 mt-0.5">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-5 bg-white rounded border">
          <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4" style={{ color: T.accent }} /><div className="font-display font-semibold text-sm" style={{ color: T.primary }}>Headcount by Department</div></div>
          <div className="space-y-2">
            {(data.by_dept || []).map((d) => {
              const max = Math.max(...(data.by_dept || []).map((x) => x.count), 1);
              return (
                <div key={d.name} className="flex items-center gap-3">
                  <div className="w-32 text-xs truncate">{d.name}</div>
                  <div className="flex-1 h-5 bg-neutral-100 rounded-sm overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${(d.count / max) * 100}%`, background: T.accent }} />
                  </div>
                  <div className="text-xs font-mono w-8 text-right">{d.count}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-5 bg-white rounded border">
          <div className="flex items-center gap-2 mb-3"><Wallet className="w-4 h-4" style={{ color: T.accent }} /><div className="font-display font-semibold text-sm" style={{ color: T.primary }}>Payroll · {data.payroll_period}</div></div>
          <div className="font-display font-bold text-2xl" style={{ color: T.primary }}>₦{(data.payroll_total || 0).toLocaleString()}</div>
          <div className="text-xs text-neutral-500">{data.payroll_count || 0} payslip{(data.payroll_count || 0) === 1 ? '' : 's'} processed</div>
          <div className="border-t my-3" />
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1.5">Employment type</div>
          {(data.emp_type || []).map((t) => (
            <div key={t.label} className="flex items-center justify-between text-sm py-0.5">
              <span className="capitalize">{(t.label || '').replace('_', ' ')}</span>
              <span className="font-mono">{t.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============== Employees ============== */
function Employees() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get('/hr/employees', { params: { q, limit: 200 } }); setItems(data.items || []); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  return (
    <div data-testid="hr-employees">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees…"
            className="w-full pl-9 pr-3 py-2 border rounded text-sm" data-testid="hr-emp-search" />
        </div>
        <button onClick={load} className="p-2 border rounded hover:bg-neutral-50"><RefreshCw className="w-4 h-4" /></button>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"
          style={{ background: T.accent, color: T.primary }} data-testid="hr-emp-create">
          <Plus className="w-4 h-4" /> New Employee
        </button>
      </div>

      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2">Emp #</th><th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Base Salary</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-neutral-400">No employees yet</td></tr>
            ) : items.map((e) => (
              <tr key={e.id} className="hover:bg-neutral-50" data-testid={`hr-emp-row-${e.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{e.employee_no}</td>
                <td className="px-3 py-2 font-medium">{e.first_name} {e.last_name}</td>
                <td className="px-3 py-2 text-neutral-600">{e.email}</td>
                <td className="px-3 py-2 text-xs">{e.employment_type}</td>
                <td className="px-3 py-2"><Badge label={e.employment_status} /></td>
                <td className="px-3 py-2 font-mono text-xs">₦{(e.base_salary || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => { setEditing(e); setShowForm(true); }} className="p-1 hover:text-blue-600"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={async () => { if (window.confirm('Delete employee?')) { await api.delete(`/hr/employees/${e.id}`); load(); } }} className="p-1 hover:text-rose-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <EmployeeForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function EmployeeForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    first_name: '', last_name: '', email: '', phone: '', gender: '',
    employment_type: 'full_time', employment_status: 'active',
    hire_date: '', base_salary: '', allowances: '',
  });
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const payload = { ...form, base_salary: parseFloat(form.base_salary) || 0, allowances: parseFloat(form.allowances) || 0 };
      if (initial) await api.patch(`/hr/employees/${initial.id}`, payload);
      else await api.post('/hr/employees', payload);
      onSaved();
    } catch (err) { setError(err?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={initial ? 'Edit employee' : 'New employee'}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name *" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required />
          <Field label="Last name *" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Gender" type="select" options={['', 'M', 'F']} value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} />
          <Field label="Employment type" type="select" options={['full_time', 'contract', 'intern', 'consultant']} value={form.employment_type} onChange={(v) => setForm({ ...form, employment_type: v })} />
          <Field label="Status" type="select" options={['active', 'probation', 'on_leave', 'suspended', 'exited']} value={form.employment_status} onChange={(v) => setForm({ ...form, employment_status: v })} />
          <Field label="Hire date" type="date" value={form.hire_date} onChange={(v) => setForm({ ...form, hire_date: v })} />
          <Field label="Base salary (₦)" type="number" value={form.base_salary} onChange={(v) => setForm({ ...form, base_salary: v })} />
          <Field label="Allowances (₦)" type="number" value={form.allowances} onChange={(v) => setForm({ ...form, allowances: v })} />
        </div>
        {error && <div className="p-2 bg-rose-50 text-rose-700 text-sm rounded">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-2 rounded text-sm font-semibold disabled:opacity-60" style={{ background: T.accent, color: T.primary }} data-testid="hr-emp-submit">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============== Attendance ============== */
function Attendance() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get('/hr/attendance', { params: { limit: 100 } }); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const clockIn = async () => { try { await api.post('/hr/attendance/clock-in', {}); load(); } catch (e) { window.alert(e?.response?.data?.detail || 'Clock-in failed'); } };
  return (
    <div data-testid="hr-attendance">
      <div className="flex justify-between mb-4">
        <p className="text-sm text-neutral-500">Recent attendance entries.</p>
        <button onClick={clockIn} className="px-4 py-2 rounded text-sm font-semibold" style={{ background: T.accent, color: T.primary }} data-testid="hr-clock-in">Quick Clock-in</button>
      </div>
      <DataTable items={items} cols={[
        { key: 'date', label: 'Date' }, { key: 'employee_id', label: 'Employee' },
        { key: 'clock_in', label: 'Clock-in' }, { key: 'clock_out', label: 'Clock-out' },
        { key: 'hours_worked', label: 'Hours' }, { key: 'status', label: 'Status', render: (v) => <Badge label={v} /> },
      ]} />
    </div>
  );
}

/* ============== Leave ============== */
function Leave() {
  const [tab, setTab] = useState('requests');
  const [items, setItems] = useState([]);
  const load = async () => {
    const { data } = await api.get(tab === 'requests' ? '/hr/leave-requests' : '/hr/leave-types');
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  const decide = async (id, kind) => { await api.post(`/hr/leave-requests/${id}/${kind}`, { comment: kind }); load(); };
  return (
    <div data-testid="hr-leave">
      <div className="flex gap-2 mb-4">
        <Tab active={tab === 'requests'} onClick={() => setTab('requests')}>Requests</Tab>
        <Tab active={tab === 'types'} onClick={() => setTab('types')}>Leave Types</Tab>
      </div>
      {tab === 'requests' ? (
        <DataTable items={items} cols={[
          { key: 'ref_no', label: 'Ref' }, { key: 'employee_id', label: 'Employee' },
          { key: 'start_date', label: 'From' }, { key: 'end_date', label: 'To' },
          { key: 'days', label: 'Days' },
          { key: 'status', label: 'Status', render: (v) => <Badge label={v} /> },
          { key: '__actions', label: '', render: (_, row) => row.status === 'pending' ? (
            <div className="flex gap-1">
              <button onClick={() => decide(row.id, 'approve')} className="p-1 text-emerald-600" title="Approve"><CheckCircle2 className="w-4 h-4" /></button>
              <button onClick={() => decide(row.id, 'reject')} className="p-1 text-rose-600" title="Reject"><XCircle className="w-4 h-4" /></button>
            </div>
          ) : null }
        ]} />
      ) : (
        <DataTable items={items} cols={[
          { key: 'name', label: 'Name' }, { key: 'days_per_year', label: 'Days/year' },
          { key: 'paid', label: 'Paid', render: (v) => v ? 'Yes' : 'No' },
        ]} />
      )}
    </div>
  );
}

/* ============== Payroll ============== */
function Payroll() {
  const [items, setItems] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const load = async () => { const { data } = await api.get('/hr/payroll', { params: { period, limit: 500 } }); setItems(data.items || []); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);
  const run = async () => { if (!window.confirm(`Run payroll for ${period}?`)) return; const { data } = await api.post('/hr/payroll/run', { period }); window.alert(`Created ${data.created} payslip(s).`); load(); };
  return (
    <div data-testid="hr-payroll">
      <div className="flex items-center gap-3 mb-4">
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="px-3 py-2 border rounded text-sm" />
        <button onClick={run} className="px-4 py-2 rounded text-sm font-semibold" style={{ background: T.accent, color: T.primary }} data-testid="hr-payroll-run">Run Payroll</button>
      </div>
      <DataTable items={items} cols={[
        { key: 'ref_no', label: 'Ref' }, { key: 'employee_id', label: 'Employee' },
        { key: 'period', label: 'Period' }, { key: 'base_salary', label: 'Base', render: (v) => `₦${(v || 0).toLocaleString()}` },
        { key: 'tax', label: 'Tax', render: (v) => `₦${(v || 0).toLocaleString()}` },
        { key: 'net_pay', label: 'Net', render: (v) => `₦${(v || 0).toLocaleString()}` },
        { key: 'status', label: 'Status', render: (v) => <Badge label={v} /> },
      ]} />
    </div>
  );
}

/* ============== Learning ============== */
function Learning() {
  const [tab, setTab] = useState('courses');
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get(tab === 'courses' ? '/hr/courses' : '/hr/enrollments'); setItems(data.items || []); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  return (
    <div data-testid="hr-learning">
      <div className="flex gap-2 mb-4">
        <Tab active={tab === 'courses'} onClick={() => setTab('courses')}>Courses</Tab>
        <Tab active={tab === 'enrollments'} onClick={() => setTab('enrollments')}>Enrollments</Tab>
      </div>
      {tab === 'courses' ? (
        <DataTable items={items} cols={[
          { key: 'name', label: 'Course' }, { key: 'provider', label: 'Provider' },
          { key: 'duration_hours', label: 'Hours' }, { key: 'category', label: 'Category' },
          { key: 'mandatory', label: 'Mandatory', render: (v) => v ? 'Yes' : 'No' },
        ]} />
      ) : (
        <DataTable items={items} cols={[
          { key: 'employee_id', label: 'Employee' }, { key: 'course_id', label: 'Course' },
          { key: 'status', label: 'Status', render: (v) => <Badge label={v} /> },
          { key: 'score', label: 'Score' },
        ]} />
      )}
    </div>
  );
}

/* ============== Candidates ============== */
function Candidates() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get('/hr/candidates'); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const hire = async (id) => { const dt = window.prompt('Hire date (YYYY-MM-DD)', new Date().toISOString().slice(0, 10)); if (!dt) return; const sal = window.prompt('Base salary (₦)', '300000'); await api.post(`/hr/candidates/${id}/hire`, { hire_date: dt, base_salary: parseFloat(sal) || 0 }); window.alert('Candidate hired and added to employee master.'); load(); };
  return (
    <div data-testid="hr-candidates">
      <DataTable items={items} cols={[
        { key: 'first_name', label: 'First name' }, { key: 'last_name', label: 'Last name' },
        { key: 'email', label: 'Email' }, { key: 'stage', label: 'Stage', render: (v) => <Badge label={v} /> },
        { key: '__hire', label: '', render: (_, row) => row.stage !== 'hired' ? (
          <button onClick={() => hire(row.id)} className="text-xs px-2 py-1 rounded" style={{ background: T.accent, color: T.primary }}>Hire</button>
        ) : <span className="text-emerald-600 text-xs">✓ Hired</span> },
      ]} />
    </div>
  );
}

/* ============== Workflows ============== */
function Workflows() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get('/hr/workflows'); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => { const comment = window.prompt(`Comment (${decision})`, ''); await api.post(`/hr/workflows/${id}/decide`, { decision, comment }); load(); };
  return (
    <div data-testid="hr-workflows">
      <DataTable items={items} cols={[
        { key: 'ref_no', label: 'Ref' }, { key: 'kind', label: 'Kind' }, { key: 'title', label: 'Title' },
        { key: 'status', label: 'Status', render: (v) => <Badge label={v} /> },
        { key: '__actions', label: '', render: (_, row) => row.status === 'pending' ? (
          <div className="flex gap-1">
            <button onClick={() => decide(row.id, 'approved')} className="p-1 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
            <button onClick={() => decide(row.id, 'rejected')} className="p-1 text-rose-600"><XCircle className="w-4 h-4" /></button>
          </div>
        ) : null },
      ]} />
    </div>
  );
}

/* ============== Generic CRUD ============== */
function SimpleCrud({ kind, title, fields, cols }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const endpoint = `/hr/${kind}`;
  const load = async () => { const { data } = await api.get(endpoint); setItems(data.items || []); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  return (
    <div data-testid={`hr-${kind}`}>
      <div className="flex justify-end mb-3">
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-3 py-1.5 rounded text-sm font-semibold flex items-center gap-1" style={{ background: T.accent, color: T.primary }}>
          <Plus className="w-4 h-4" /> New
        </button>
      </div>
      <DataTable items={items} cols={cols.map((c) => ({ key: c, label: c.replace(/_/g, ' ') }))} onEdit={(row) => { setEditing(row); setShowForm(true); }} onDelete={async (row) => { if (window.confirm('Delete?')) { await api.delete(`${endpoint}/${row.id}`); load(); } }} />
      {showForm && (
        <Modal onClose={() => setShowForm(false)} title={editing ? `Edit ${title.slice(0, -1)}` : `New ${title.slice(0, -1)}`}>
          <CrudForm fields={fields} initial={editing} onCancel={() => setShowForm(false)} onSubmit={async (vals) => {
            try {
              if (editing) await api.patch(`${endpoint}/${editing.id}`, vals);
              else await api.post(endpoint, vals);
              setShowForm(false); load();
            } catch (e) { window.alert(e?.response?.data?.detail || 'Save failed'); }
          }} />
        </Modal>
      )}
    </div>
  );
}

function CrudForm({ fields, initial, onCancel, onSubmit }) {
  const [vals, setVals] = useState(() => fields.reduce((a, f) => ({ ...a, [f.key]: initial?.[f.key] ?? '' }), {}));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { const cast = {}; for (const f of fields) { let v = vals[f.key]; if (f.type === 'number' && v !== '') v = parseFloat(v); cast[f.key] = v; } await onSubmit(cast); } finally { setBusy(false); } };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {fields.map((f) => (
          f.type === 'employee' ? (
            <div key={f.key} className="col-span-2">
              <EmployeeSearchPicker
                label={f.label}
                value={vals[f.key]}
                required={f.required}
                onSelect={(emp) => setVals({ ...vals, [f.key]: emp?.id || '' })}
                testid={`picker-${f.key}`}
              />
            </div>
          ) : (
            <Field key={f.key} label={f.label} type={f.type || 'text'} options={f.options} value={vals[f.key]} onChange={(v) => setVals({ ...vals, [f.key]: v })} required={f.required} />
          )
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded text-sm">Cancel</button>
        <button type="submit" disabled={busy} className="px-4 py-2 rounded text-sm font-semibold disabled:opacity-60" style={{ background: T.accent, color: T.primary }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/* ============== Shared UI primitives ============== */
function DataTable({ items, cols, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
          <tr>{cols.map((c) => <th key={c.key} className="text-left px-3 py-2">{c.label}</th>)}
          {(onEdit || onDelete) && <th></th>}</tr>
        </thead>
        <tbody className="divide-y">
          {items.length === 0 ? (
            <tr><td colSpan={cols.length + 1} className="text-center py-8 text-neutral-400">No records</td></tr>
          ) : items.map((row) => (
            <tr key={row.id} className="hover:bg-neutral-50">
              {cols.map((c) => <td key={c.key} className="px-3 py-2">{c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}</td>)}
              {(onEdit || onDelete) && (
                <td className="px-3 py-2 text-right">
                  {onEdit && <button onClick={() => onEdit(row)} className="p-1 hover:text-blue-600"><Edit2 className="w-3.5 h-3.5" /></button>}
                  {onDelete && <button onClick={() => onDelete(row)} className="p-1 hover:text-rose-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, type = 'text', value, onChange, options, required }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1">{label}</span>
      {type === 'textarea' ? (
        <textarea value={value || ''} required={required} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-3 py-2 border rounded text-sm" />
      ) : type === 'select' ? (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border rounded text-sm bg-white">
          {(options || []).map((o) => <option key={o} value={o}>{o || '—'}</option>)}
        </select>
      ) : (
        <input type={type} value={value || ''} required={required} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
      )}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="font-display font-semibold text-navy">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Badge({ label }) {
  const colours = {
    active: 'bg-emerald-100 text-emerald-700', approved: 'bg-emerald-100 text-emerald-700',
    paid: 'bg-emerald-100 text-emerald-700', completed: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700', probation: 'bg-amber-100 text-amber-700',
    enrolled: 'bg-blue-100 text-blue-700', in_progress: 'bg-blue-100 text-blue-700',
    rejected: 'bg-rose-100 text-rose-700', exited: 'bg-rose-100 text-rose-700',
    suspended: 'bg-rose-100 text-rose-700', failed: 'bg-rose-100 text-rose-700',
    on_leave: 'bg-violet-100 text-violet-700', open: 'bg-blue-100 text-blue-700',
    draft: 'bg-neutral-100 text-neutral-600', processed: 'bg-cyan-100 text-cyan-700',
  };
  const cls = colours[label] || 'bg-neutral-100 text-neutral-600';
  return <span className={`inline-block text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}

function Tab({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 rounded text-sm font-medium ${active ? 'text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
      style={active ? { background: T.primary } : {}}>{children}</button>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="bg-white border border-dashed rounded p-10 text-center">
      <div className="font-display font-semibold text-lg mb-2" style={{ color: T.primary }}>{title}</div>
      <p className="text-sm text-neutral-500 max-w-md mx-auto">{desc}</p>
    </div>
  );
}

/* ============== Devices (with Test connection action) ============== */
function Devices() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get('/hr/devices', { params: { limit: 200 } }); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const submit = async (body) => {
    if (editing) await api.patch(`/hr/devices/${editing.id}`, body);
    else await api.post('/hr/devices', body);
    setShowForm(false); setEditing(null); load();
  };
  const test = async (id) => {
    try { const { data } = await api.post(`/hr/devices/${id}/test`); alert(`Device ${data.reachable ? 'reachable' : 'unreachable'} (stub). Last seen: ${data.tested_at}`); load(); }
    catch (e) { alert('Test failed'); }
  };
  const remove = async (id) => { if (window.confirm('Delete device?')) { await api.delete(`/hr/devices/${id}`); load(); } };
  const fields = [
    { key: 'name', label: 'Device name', required: true },
    { key: 'device_type', label: 'Type', type: 'select', options: ['biometric', 'fingerprint', 'face', 'card_reader', 'keypad', 'turnstile', 'door', 'kiosk', 'mobile', 'qr_scanner'] },
    { key: 'serial_number', label: 'Serial number' },
    { key: 'mac_address', label: 'MAC address' },
    { key: 'ip_address', label: 'IP address' },
    { key: 'port', label: 'Port', type: 'number' },
    { key: 'location', label: 'Location' },
    { key: 'manufacturer', label: 'Manufacturer' },
    { key: 'firmware_version', label: 'Firmware version' },
    { key: 'status', label: 'Status', type: 'select', options: ['active', 'disabled', 'maintenance', 'offline'] },
  ];
  return (
    <div data-testid="hr-devices">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Device Registry</h2>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-accent" data-testid="add-device-btn"><Plus className="w-4 h-4" /> Add device</button>
      </div>
      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-neutral-50/60">
            <tr><th className="text-left px-3 py-2.5">Name</th><th className="text-left px-3 py-2.5">Type</th><th className="text-left px-3 py-2.5">IP : Port</th><th className="text-left px-3 py-2.5">Location</th><th className="text-left px-3 py-2.5">Status</th><th className="text-left px-3 py-2.5">Last seen</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b hover:bg-neutral-50/40" data-testid={`hr-device-row-${d.id}`}>
                <td className="px-3 py-2.5 font-medium">{d.name}<div className="text-[10px] text-neutral-400 font-mono">{d.serial_number || '—'}</div></td>
                <td className="px-3 py-2.5"><span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded" style={{ background: `${T.accent}18`, color: T.primary }}>{d.device_type}</span></td>
                <td className="px-3 py-2.5 font-mono text-xs">{d.ip_address || '—'}{d.port ? `:${d.port}` : ''}</td>
                <td className="px-3 py-2.5">{d.location || '—'}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${d.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}`}>{d.status}</span></td>
                <td className="px-3 py-2.5 text-xs text-neutral-500">{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : '—'}</td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={() => test(d.id)} className="text-[10px] uppercase font-semibold px-2 py-1 rounded border border-neutral-200 hover:bg-navy hover:text-white mr-1" data-testid={`test-device-${d.id}`}>Test</button>
                  <button onClick={() => { setEditing(d); setShowForm(true); }} className="p-1 hover:bg-neutral-100 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(d.id)} className="p-1 hover:bg-rose-50 text-rose-500 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-neutral-400">No devices registered yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <Modal title={editing ? 'Edit device' : 'Add device'} onClose={() => { setShowForm(false); setEditing(null); }}><CrudForm fields={fields} initial={editing} onCancel={() => { setShowForm(false); setEditing(null); }} onSubmit={submit} /></Modal>}
    </div>
  );
}

/* ============== Visitors (with check-in / check-out actions) ============== */
function Visitors() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = async () => { const { data } = await api.get('/hr/visitors', { params: { limit: 200 } }); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const submit = async (body) => { await api.post('/hr/visitors', body); setShowForm(false); load(); };
  const checkIn = async (id) => { await api.post(`/hr/visitors/${id}/check-in`); load(); };
  const checkOut = async (id) => { await api.post(`/hr/visitors/${id}/check-out`); load(); };
  const fields = [
    { key: 'name', label: 'Visitor name', required: true },
    { key: 'phone', label: 'Phone' },
    { key: 'organization', label: 'Visiting from (org)' },
    { key: 'purpose', label: 'Purpose of visit' },
    { key: 'destination', label: 'Destination office / room' },
    { key: 'host_employee_id', label: 'Host employee ID' },
    { key: 'scheduled_at', label: 'Scheduled at', type: 'datetime-local' },
  ];
  const statusColor = (s) => ({ pre_registered: 'bg-amber/10 text-amber-700', checked_in: 'bg-emerald-100 text-emerald-700', checked_out: 'bg-neutral-100 text-neutral-500', cancelled: 'bg-rose-100 text-rose-700', denied: 'bg-rose-100 text-rose-700' })[s] || 'bg-neutral-100 text-neutral-500';
  return (
    <div data-testid="hr-visitors">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Visitor Management</h2>
        <button onClick={() => setShowForm(true)} className="btn-accent" data-testid="add-visitor-btn"><Plus className="w-4 h-4" /> Pre-register visitor</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((v) => (
          <div key={v.id} className="bg-white border rounded p-4" data-testid={`hr-visitor-row-${v.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-semibold text-lg" style={{ color: T.primary }}>{v.name}</div>
                <div className="text-xs text-neutral-500">{v.organization || 'Walk-in'} · {v.phone || '—'}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${statusColor(v.status)}`}>{v.status?.replace('_', ' ')}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <div><span className="text-neutral-400">Purpose</span><div className="font-medium">{v.purpose || '—'}</div></div>
              <div><span className="text-neutral-400">Destination</span><div className="font-medium">{v.destination || '—'}</div></div>
              <div><span className="text-neutral-400">QR Code</span><div className="font-mono">{v.qr_code}</div></div>
              <div><span className="text-neutral-400">PIN</span><div className="font-mono text-base font-bold">{v.pin}</div></div>
            </div>
            <div className="flex gap-2">
              {v.status === 'pre_registered' && <button onClick={() => checkIn(v.id)} className="text-[11px] font-semibold uppercase px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`check-in-${v.id}`}>Check in</button>}
              {v.status === 'checked_in' && <button onClick={() => checkOut(v.id)} className="text-[11px] font-semibold uppercase px-3 py-1.5 rounded bg-neutral-700 text-white hover:bg-neutral-800" data-testid={`check-out-${v.id}`}>Check out</button>}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="lg:col-span-2 bg-white border border-dashed rounded p-10 text-center text-neutral-400">No visitors yet. Pre-register your first.</div>}
      </div>
      {showForm && <Modal title="Pre-register visitor" onClose={() => setShowForm(false)}><CrudForm fields={fields} initial={null} onCancel={() => setShowForm(false)} onSubmit={submit} /></Modal>}
    </div>
  );
}

/* ============== Access Events (read-only feed) ============== */
function AccessEvents() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get('/hr/access-events', { params: { limit: 100 } }); setItems(data.items || []); };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  return (
    <div data-testid="hr-access-events">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Access Events</h2>
          <p className="text-sm text-neutral-500">Live feed from registered devices · refresh every 8s</p>
        </div>
        <button onClick={load} className="text-xs uppercase font-semibold px-2 py-1 rounded border hover:bg-neutral-100"><RefreshCw className="w-3 h-3 inline" /> Refresh</button>
      </div>
      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-neutral-50/60">
            <tr><th className="text-left px-3 py-2.5">When</th><th className="text-left px-3 py-2.5">Employee #</th><th className="text-left px-3 py-2.5">Action</th><th className="text-left px-3 py-2.5">Method</th><th className="text-left px-3 py-2.5">Device</th><th className="text-left px-3 py-2.5">Location</th><th className="text-left px-3 py-2.5">Result</th></tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-b hover:bg-neutral-50/40">
                <td className="px-3 py-2.5 text-xs text-neutral-500">{new Date(e.timestamp).toLocaleString()}</td>
                <td className="px-3 py-2.5 font-mono">{e.employee_no || '—'}</td>
                <td className="px-3 py-2.5"><span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-navy/10 text-navy">{e.action}</span></td>
                <td className="px-3 py-2.5 text-xs">{e.method}</td>
                <td className="px-3 py-2.5">{e.device_name}</td>
                <td className="px-3 py-2.5">{e.location || '—'}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${e.granted ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{e.granted ? 'granted' : 'denied'}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-neutral-400">No device events yet. Register a device and POST to <span className="font-mono">/api/hr/devices/&#123;id&#125;/events</span></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============== Config Center (Phase D) ============== */
function ConfigSection({ title, desc, children }) {
  return (
    <div className="bg-white border rounded p-5">
      <div className="mb-3">
        <h3 className="font-display font-semibold text-lg" style={{ color: T.primary }}>{title}</h3>
        <p className="text-xs text-neutral-500 mt-0.5">{desc}</p>
      </div>
      {children}
    </div>
  );
}
function ConfigInput({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <label className="block text-sm mb-3">
      <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-1">{label}</div>
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-neutral-200 rounded outline-none focus:border-navy text-sm" />
    </label>
  );
}
function ConfigTextarea({ label, value, onChange, placeholder, rows = 4 }) {
  return (
    <label className="block text-sm mb-3">
      <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-1">{label}</div>
      <textarea rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-neutral-200 rounded outline-none focus:border-navy text-sm font-mono" />
    </label>
  );
}
function ConfigCenter() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const { data } = await api.get('/hr/config'); setCfg(data); } catch (e) { console.error(e); setCfg({ branding: {}, templates: {}, workflow_rules: {} }); } };
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="text-neutral-500">Loading…</div>;
  const set = (section, key, value) => setCfg((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
  const get = (section, key) => (cfg[section] || {})[key];
  const save = async () => {
    setBusy(true);
    try { await api.put('/hr/config', { branding: cfg.branding || {}, templates: cfg.templates || {}, workflow_rules: cfg.workflow_rules || {} }); alert('Configuration saved.'); }
    catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="hr-config" className="max-w-4xl">
      <div className="flex justify-between items-start mb-5">
        <div>
          <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Universal Configuration Center</h2>
          <p className="text-sm text-neutral-500">Org branding, document templates, and workflow rules — used by every module in this organisation.</p>
        </div>
        <button onClick={save} disabled={busy} className="btn-accent" data-testid="save-config">{busy ? 'Saving…' : 'Save changes'}</button>
      </div>
      <div className="space-y-4">
        <ConfigSection title="Organisation branding" desc="Used on invoice, payslip, certificate and offer-letter PDFs">
          <div className="grid grid-cols-2 gap-3">
            <ConfigInput label="Logo URL" value={get('branding', 'logo_url')} onChange={(v) => set('branding', 'logo_url', v)} placeholder="https://…/logo.png" />
            <ConfigInput label="Letterhead URL" value={get('branding', 'letterhead_url')} onChange={(v) => set('branding', 'letterhead_url', v)} placeholder="https://…/letterhead.png" />
            <ConfigInput label="Stamp URL" value={get('branding', 'stamp_url')} onChange={(v) => set('branding', 'stamp_url', v)} placeholder="https://…/stamp.png" />
            <ConfigInput label="Signature URL" value={get('branding', 'signature_url')} onChange={(v) => set('branding', 'signature_url', v)} placeholder="https://…/sig.png" />
            <ConfigInput label="Brand colour primary" value={get('branding', 'brand_primary')} onChange={(v) => set('branding', 'brand_primary', v)} placeholder="#0D2A4D" />
            <ConfigInput label="Brand colour accent" value={get('branding', 'brand_accent')} onChange={(v) => set('branding', 'brand_accent', v)} placeholder="#E0B43C" />
            <ConfigInput label="Tagline" value={get('branding', 'tagline')} onChange={(v) => set('branding', 'tagline', v)} placeholder="One workforce. One system." />
            <ConfigInput label="Public phone" value={get('branding', 'public_phone')} onChange={(v) => set('branding', 'public_phone', v)} placeholder="+234 1 234 5678" />
          </div>
        </ConfigSection>
        <ConfigSection title="Document templates" desc="Header / footer text per document type. Branding is auto-merged.">
          <ConfigTextarea label="Invoice header" value={get('templates', 'invoice_header')} onChange={(v) => set('templates', 'invoice_header', v)} placeholder="ATTP Nexus Pvt Ltd · RC 1234567 · Vat 1234567890" />
          <ConfigTextarea label="Invoice footer" value={get('templates', 'invoice_footer')} onChange={(v) => set('templates', 'invoice_footer', v)} placeholder="Thank you for your business. Pay to: …" />
          <ConfigTextarea label="Payslip footer" value={get('templates', 'payslip_footer')} onChange={(v) => set('templates', 'payslip_footer', v)} placeholder="Computer-generated, no signature required." />
          <ConfigTextarea label="Offer-letter template" value={get('templates', 'offer_letter')} onChange={(v) => set('templates', 'offer_letter', v)} rows={6} placeholder="Dear {name}, we are pleased to offer you …" />
        </ConfigSection>
        <ConfigSection title="Workflow rules" desc="Approval & escalation defaults for HR processes">
          <div className="grid grid-cols-2 gap-3">
            <ConfigInput label="Leave approval SLA (hrs)" type="number" value={get('workflow_rules', 'leave_sla_hours')} onChange={(v) => set('workflow_rules', 'leave_sla_hours', v)} placeholder="48" />
            <ConfigInput label="Escalate after (hrs)" type="number" value={get('workflow_rules', 'escalate_after_hours')} onChange={(v) => set('workflow_rules', 'escalate_after_hours', v)} placeholder="24" />
            <ConfigInput label="Payroll cut-off day" type="number" value={get('workflow_rules', 'payroll_cutoff_day')} onChange={(v) => set('workflow_rules', 'payroll_cutoff_day', v)} placeholder="25" />
            <ConfigInput label="Pay date day-of-month" type="number" value={get('workflow_rules', 'pay_date_day')} onChange={(v) => set('workflow_rules', 'pay_date_day', v)} placeholder="28" />
          </div>
        </ConfigSection>
      </div>
    </div>
  );
}


/* ============== HMO Change Requests (approve/reject) ============== */
function HMOChangeRequests() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = async () => { const { data } = await api.get('/hr/hmo/change-requests', { params: { limit: 200 } }); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const submit = async (body) => { await api.post('/hr/hmo/change-requests', body); setShowForm(false); load(); };
  const decide = async (id, decision) => {
    const notes = decision === 'rejected' ? window.prompt('Rejection notes (optional):') : null;
    await api.post(`/hr/hmo/change-requests/${id}/decide`, { decision, notes });
    load();
  };
  const fields = [
    { key: 'enrollment_id', label: 'Enrollment ID', required: true },
    { key: 'employee_id', label: 'Employee ID', required: true },
    { key: 'current_hospital_id', label: 'Current hospital ID' },
    { key: 'requested_hospital_id', label: 'Requested hospital ID', required: true },
    { key: 'reason', label: 'Reason', type: 'textarea' },
  ];
  const statusColor = (s) => ({ pending: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-rose-100 text-rose-700' })[s] || 'bg-neutral-100';
  return (
    <div data-testid="hr-hmo-change-requests">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Hospital Change Requests</h2>
        <button onClick={() => setShowForm(true)} className="btn-accent" data-testid="add-change-req-btn"><Plus className="w-4 h-4" /> New request</button>
      </div>
      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-neutral-50/60"><tr>
            <th className="text-left px-3 py-2.5">Employee</th><th className="text-left px-3 py-2.5">From hospital</th>
            <th className="text-left px-3 py-2.5">To hospital</th><th className="text-left px-3 py-2.5">Reason</th>
            <th className="text-left px-3 py-2.5">Status</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b hover:bg-neutral-50/40">
                <td className="px-3 py-2.5 font-mono text-xs">{r.employee_id?.slice(0, 8)}…</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.current_hospital_id?.slice(0, 8) || '—'}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.requested_hospital_id?.slice(0, 8)}…</td>
                <td className="px-3 py-2.5 text-xs">{r.reason || '—'}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${statusColor(r.status)}`}>{r.status}</span></td>
                <td className="px-3 py-2.5 text-right">
                  {r.status === 'pending' && (
                    <>
                      <button onClick={() => decide(r.id, 'approved')} className="text-[10px] uppercase font-semibold px-2 py-1 rounded bg-emerald-600 text-white mr-1" data-testid={`approve-${r.id}`}>Approve</button>
                      <button onClick={() => decide(r.id, 'rejected')} className="text-[10px] uppercase font-semibold px-2 py-1 rounded bg-rose-600 text-white" data-testid={`reject-${r.id}`}>Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-neutral-400">No change requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <Modal title="New hospital change request" onClose={() => setShowForm(false)}><CrudForm fields={fields} initial={null} onCancel={() => setShowForm(false)} onSubmit={submit} /></Modal>}
    </div>
  );
}


/* ============== Templates Tab — Bulk download/upload (Phase E) ============== */
function TemplatesTab() {
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const KINDS = [
    { key: 'employees', label: 'Employees', desc: 'Bulk-onboard staff with personal, employment, salary & bank details' },
    { key: 'departments', label: 'Departments', desc: 'Bulk-create organisational departments and codes' },
    { key: 'positions', label: 'Positions', desc: 'Job roles with grade bands and salary ranges' },
    { key: 'hmo_providers', label: 'HMO Providers', desc: 'Health Maintenance Organisations and coverage tiers' },
  ];
  const download = async (kind) => {
    setBusy(`d:${kind}`);
    try {
      const r = await api.get(`/hr/templates/${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `ATTP-${kind}-template.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(`Download failed: ${e.message}`); } finally { setBusy(null); }
  };
  const upload = async (kind, file) => {
    setBusy(`u:${kind}`);
    setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post(`/hr/templates/${kind}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult({ kind, ...data });
    } catch (e) { setResult({ kind, ok: false, error: e?.response?.data?.detail || e.message }); } finally { setBusy(null); }
  };
  return (
    <div data-testid="hr-templates">
      <div className="mb-5">
        <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Bulk Templates</h2>
        <p className="text-sm text-neutral-500">Download an XLSX template, fill it externally, then upload it back. The system validates row-by-row and returns a downloadable error report for rejected lines.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {KINDS.map((k) => (
          <div key={k.key} className="bg-white border rounded p-5" data-testid={`tpl-card-${k.key}`}>
            <div className="font-display font-semibold text-lg" style={{ color: T.primary }}>{k.label}</div>
            <p className="text-xs text-neutral-500 mt-1 mb-4">{k.desc}</p>
            <div className="flex gap-2">
              <button onClick={() => download(k.key)} disabled={busy === `d:${k.key}`}
                className="btn-outline text-xs flex-1 justify-center" data-testid={`tpl-download-${k.key}`}>
                <Download className="w-3.5 h-3.5" />{busy === `d:${k.key}` ? '…' : 'Download'}
              </button>
              <label className="btn-accent text-xs flex-1 justify-center cursor-pointer" data-testid={`tpl-upload-${k.key}`}>
                <Upload className="w-3.5 h-3.5" />
                {busy === `u:${k.key}` ? 'Uploading…' : 'Upload filled'}
                <input type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && upload(k.key, e.target.files[0])} />
              </label>
            </div>
          </div>
        ))}
      </div>
      {result && (
        <div className="mt-6 bg-white border rounded p-5" data-testid="tpl-result">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-display font-semibold text-lg" style={{ color: T.primary }}>Import result · {result.kind}</h3>
            <button onClick={() => setResult(null)} className="text-xs uppercase font-semibold px-2 py-1 rounded hover:bg-neutral-100"><X className="w-3 h-3 inline" /></button>
          </div>
          {result.ok ? (
            <div>
              <div className="flex gap-3 mb-3">
                <div className="px-3 py-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold">Created: {result.created}</div>
                <div className="px-3 py-2 rounded bg-rose-50 border border-rose-200 text-rose-700 text-sm font-semibold">Errors: {result.error_count}</div>
              </div>
              {result.errors?.length > 0 && (
                <div className="bg-rose-50/40 border border-rose-100 rounded p-3 max-h-72 overflow-y-auto">
                  <div className="text-xs font-semibold uppercase tracking-wider text-rose-800 mb-2">Rejected rows</div>
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-xs mb-1.5 pb-1.5 border-b border-rose-100 last:border-0">
                      <span className="font-mono font-semibold">Row {e.row}</span> — {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">{result.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============== Invoices (with PDF + Mark paid) ============== */
function InvoicesTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get('/hr/invoices', { params: { limit: 200 } }); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const submit = async (body) => {
    if (typeof body.items_json === 'string') {
      try { body.items = JSON.parse(body.items_json); } catch { body.items = []; }
      delete body.items_json;
    }
    if (editing) await api.patch(`/hr/invoices/${editing.id}`, body);
    else await api.post('/hr/invoices', body);
    setShowForm(false); setEditing(null); load();
  };
  const markPaid = async (id) => {
    const ref = window.prompt('Payment reference (optional):') || undefined;
    await api.post(`/hr/invoices/${id}/mark-paid`, { payment_method: 'bank_transfer', payment_ref: ref });
    load();
  };
  const downloadPDF = async (id, no) => {
    try {
      const r = await api.get(`/hr/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${no || 'invoice'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed'); }
  };
  const remove = async (id) => { if (window.confirm('Delete invoice?')) { await api.delete(`/hr/invoices/${id}`); load(); } };
  const fields = [
    { key: 'customer_name', label: 'Customer name', required: true },
    { key: 'customer_email', label: 'Customer email' },
    { key: 'customer_address', label: 'Customer address', type: 'textarea' },
    { key: 'due_date', label: 'Due date', type: 'date' },
    { key: 'currency', label: 'Currency', type: 'select', options: ['NGN', 'USD', 'EUR', 'GBP'] },
    { key: 'items_json', label: 'Line items (JSON array)', type: 'textarea' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ];
  const statusColor = (s) => ({ draft: 'bg-neutral-100 text-neutral-600', sent: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-rose-100 text-rose-700' })[s] || 'bg-neutral-100';
  return (
    <div data-testid="hr-invoices">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Invoices</h2>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-accent" data-testid="add-invoice-btn"><Plus className="w-4 h-4" /> New invoice</button>
      </div>
      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-neutral-50/60">
            <tr><th className="text-left px-3 py-2.5">Invoice #</th><th className="text-left px-3 py-2.5">Customer</th><th className="text-left px-3 py-2.5">Issue</th><th className="text-left px-3 py-2.5">Due</th><th className="text-right px-3 py-2.5">Total</th><th className="text-left px-3 py-2.5">Status</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((inv) => (
              <tr key={inv.id} className="border-b hover:bg-neutral-50/40">
                <td className="px-3 py-2.5 font-mono text-xs">{inv.invoice_no}</td>
                <td className="px-3 py-2.5 font-medium">{inv.customer_name}<div className="text-[11px] text-neutral-500">{inv.customer_email || ''}</div></td>
                <td className="px-3 py-2.5 text-xs">{inv.issue_date}</td>
                <td className="px-3 py-2.5 text-xs">{inv.due_date || '—'}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{inv.currency || 'NGN'} {(inv.total || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${statusColor(inv.status)}`}>{inv.status}</span></td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => downloadPDF(inv.id, inv.invoice_no)} className="text-[10px] uppercase font-semibold px-2 py-1 rounded border hover:bg-navy hover:text-white mr-1" data-testid={`pdf-${inv.id}`}><Download className="w-3 h-3 inline" /> PDF</button>
                  {inv.status !== 'paid' && <button onClick={() => markPaid(inv.id)} className="text-[10px] uppercase font-semibold px-2 py-1 rounded bg-emerald-600 text-white mr-1" data-testid={`pay-${inv.id}`}>Mark paid</button>}
                  <button onClick={() => remove(inv.id)} className="p-1 hover:bg-rose-50 text-rose-500 rounded"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-neutral-400">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <Modal title="New invoice" onClose={() => { setShowForm(false); setEditing(null); }}>
        <CrudForm fields={fields} initial={editing || { items_json: '[{"description":"HR Consultancy","quantity":1,"unit_price":500000,"tax_rate":7.5}]' }} onCancel={() => { setShowForm(false); setEditing(null); }} onSubmit={submit} />
      </Modal>}
    </div>
  );
}

/* ============== Receipts ============== */
function ReceiptsTab() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get('/hr/receipts', { params: { limit: 200 } }); setItems(data.items || []); };
  useEffect(() => { load(); }, []);
  const downloadPDF = async (id, no) => {
    try {
      const r = await api.get(`/hr/receipts/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${no || 'receipt'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed'); }
  };
  return (
    <div data-testid="hr-receipts">
      <div className="mb-4">
        <h2 className="font-display font-bold text-2xl" style={{ color: T.primary }}>Receipts</h2>
        <p className="text-sm text-neutral-500">Auto-generated when an invoice is marked Paid.</p>
      </div>
      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-neutral-50/60"><tr>
            <th className="text-left px-3 py-2.5">Receipt #</th><th className="text-left px-3 py-2.5">For invoice</th>
            <th className="text-left px-3 py-2.5">Customer</th><th className="text-right px-3 py-2.5">Amount</th>
            <th className="text-left px-3 py-2.5">Method</th><th className="text-left px-3 py-2.5">Issued</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b hover:bg-neutral-50/40">
                <td className="px-3 py-2.5 font-mono text-xs">{r.receipt_no}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.invoice_no || '—'}</td>
                <td className="px-3 py-2.5">{r.customer_name}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{r.currency || 'NGN'} {(r.amount || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-xs">{r.payment_method}</td>
                <td className="px-3 py-2.5 text-xs">{(r.issued_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="px-3 py-2.5 text-right"><button onClick={() => downloadPDF(r.id, r.receipt_no)} className="text-[10px] uppercase font-semibold px-2 py-1 rounded border hover:bg-navy hover:text-white"><Download className="w-3 h-3 inline" /> PDF</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-neutral-400">No receipts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============== AI Compliance Q&A ============== */
function AICompliance() {
  const [question, setQuestion] = useState('');
  const [country, setCountry] = useState('Nigeria');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (question.trim().length < 5) return;
    setBusy(true); setAnswer('');
    try {
      const { data } = await api.post('/hr/ai/compliance', { question, country });
      setAnswer(data.answer || '');
    } catch (e) { setAnswer(`Error: ${e?.response?.data?.detail || e.message}`); } finally { setBusy(false); }
  };
  const examples = [
    'What is the maternity leave compliance for permanent employees?',
    'How should overtime be calculated for senior salaried staff?',
    'What are the PAYE bands for 2026?',
    'What pension contributions are mandatory?',
  ];
  return (
    <div data-testid="hr-ai-compliance" className="max-w-4xl">
      <div className="mb-5">
        <h2 className="font-display font-bold text-2xl flex items-center gap-2" style={{ color: T.primary }}><Sparkles className="w-6 h-6 text-amber-500" /> AI Compliance Assistant</h2>
        <p className="text-sm text-neutral-500">Ask labour-law, tax, and HR compliance questions for your country. Always verify with a labour lawyer for binding decisions.</p>
      </div>
      <div className="bg-white border rounded p-5 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Country</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="px-3 py-1.5 border rounded text-sm">
            <option>Nigeria</option><option>Ghana</option><option>Kenya</option><option>South Africa</option><option>Egypt</option>
            <option>United Kingdom</option><option>United States</option>
          </select>
        </div>
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={3} placeholder="e.g. What is the maternity leave compliance for permanent employees?"
          className="w-full px-3 py-2 border rounded outline-none focus:border-navy text-sm" data-testid="ai-compliance-input" />
        <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
          {examples.map((ex) => <button key={ex} onClick={() => setQuestion(ex)} className="text-[10px] uppercase font-semibold px-2 py-1 rounded border hover:bg-neutral-100 text-neutral-600">{ex}</button>)}
        </div>
        <button onClick={ask} disabled={busy || question.trim().length < 5} className="btn-accent" data-testid="ai-compliance-ask">{busy ? 'Thinking…' : 'Ask the assistant'}</button>
      </div>
      {answer && (
        <div className="bg-white border rounded p-5" data-testid="ai-compliance-answer">
          <h3 className="font-display font-semibold text-lg mb-3" style={{ color: T.primary }}>Answer</h3>
          <div className="text-sm leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: answer.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
          <p className="mt-4 text-[11px] text-neutral-500 italic">AI-generated answer · always verify with a labour lawyer for binding HR decisions.</p>
        </div>
      )}
    </div>
  );
}

/* ============== AI Hiring Screening ============== */
function AIScreening() {
  const [cv, setCv] = useState('');
  const [jd, setJd] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const verdictColor = (v) => ({ 'Highly Suitable': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Suitable': 'bg-blue-100 text-blue-700 border-blue-300',
    'Weak Match': 'bg-amber-100 text-amber-700 border-amber-300',
    'Not Suitable': 'bg-rose-100 text-rose-700 border-rose-300' })[v] || 'bg-neutral-100 text-neutral-700';
  const screen = async () => {
    if (cv.length < 30 || jd.length < 30) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post('/hr/ai/screen-candidate', { candidate_name: name, cv_text: cv, job_description: jd });
      setResult(data);
    } catch (e) { setResult({ error: e?.response?.data?.detail || e.message }); } finally { setBusy(false); }
  };
  return (
    <div data-testid="hr-ai-screening" className="max-w-5xl">
      <div className="mb-5">
        <h2 className="font-display font-bold text-2xl flex items-center gap-2" style={{ color: T.primary }}><Sparkles className="w-6 h-6 text-amber-500" /> AI Hiring Screening</h2>
        <p className="text-sm text-neutral-500">Paste a candidate CV + job description. Get a verdict, score (0–100), strengths, and gaps.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border rounded p-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Candidate name (optional)" className="w-full mb-2 px-3 py-1.5 border rounded text-sm" />
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-neutral-600 mb-1">CV / résumé text</label>
          <textarea value={cv} onChange={(e) => setCv(e.target.value)} rows={12} placeholder="Paste candidate CV here…" className="w-full px-3 py-2 border rounded outline-none focus:border-navy text-sm font-mono" data-testid="ai-screen-cv" />
        </div>
        <div className="bg-white border rounded p-4">
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-neutral-600 mb-1">Job description</label>
          <textarea value={jd} onChange={(e) => setJd(e.target.value)} rows={13} placeholder="Paste job description here…" className="w-full px-3 py-2 border rounded outline-none focus:border-navy text-sm" data-testid="ai-screen-jd" />
        </div>
      </div>
      <button onClick={screen} disabled={busy || cv.length < 30 || jd.length < 30} className="btn-accent" data-testid="ai-screen-go">{busy ? 'Screening…' : 'Run AI screening'}</button>
      {result && !result.error && (
        <div className="mt-5 bg-white border rounded p-5" data-testid="ai-screen-result">
          <div className="flex items-center gap-4 mb-4">
            <div className={`px-3 py-1.5 rounded border font-bold text-sm ${verdictColor(result.verdict)}`}>{result.verdict || 'No verdict'}</div>
            {result.score != null && (
              <div className="flex items-center gap-2">
                <div className="text-3xl font-bold tabular-nums" style={{ color: T.primary }}>{result.score}</div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">/ 100</div>
              </div>
            )}
          </div>
          {result.rationale && <p className="text-sm leading-relaxed mb-4">{result.rationale}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-1.5">Strengths</h4>
              <ul className="text-sm space-y-1">{(result.strengths || []).map((s, i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />{s}</li>)}</ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-wider font-semibold text-rose-700 mb-1.5">Gaps</h4>
              <ul className="text-sm space-y-1">{(result.gaps || []).map((s, i) => <li key={i} className="flex items-start gap-1.5"><XCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5 flex-shrink-0" />{s}</li>)}</ul>
            </div>
          </div>
        </div>
      )}
      {result?.error && <div className="mt-5 p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">{result.error}</div>}
    </div>
  );
}

